import React from "react";
import PolicyPage from "../../components/policy/PolicyPage";
import { BUSINESS } from "../../config/business";

export default function ShippingPolicy() {
  const sections = [
    {
      heading: "Where we deliver",
      body: [
        `${BUSINESS.brand} sells and delivers within ${BUSINESS.serviceArea}. We are a showroom-led local retailer, not a pan-India e-commerce seller, and we deliver only where we can also service and support what we sell.`,
        {
          list: [
            "Showroom pickup — from our showroom at " + BUSINESS.operatingAddress + ". Available for every product, at no charge.",
            "Home delivery in Patna city — available for e-cycles, bicycles, components and accessories.",
            "Delivery to adjoining districts of Bihar — available on request, subject to distance and a delivery charge quoted before you confirm.",
            "Outside this area — we do not deliver. If you are outside our service area, contact us and we will put you in touch with the nearest authorised dealer for the brand you want.",
          ],
        },
        {
          note:
            "We do not ship internationally, and we do not export. See section 7 and our Terms of Service for the restrictions that apply to lithium-ion batteries.",
        },
      ],
    },
    {
      heading: "Delivery charges",
      body: [
        `All charges are quoted and payable in ${BUSINESS.currencyLabel}.`,
        {
          table: {
            head: ["Option", "Charge"],
            rows: [
              ["Showroom pickup", "Free"],
              [
                "Home delivery within Patna city limits",
                "Free on e-cycles and bicycles. A nominal charge may apply to small accessory-only orders, and is shown before you confirm",
              ],
              [
                "Delivery outside Patna city, within our service area",
                "Quoted case by case based on distance, and confirmed to you in writing before you pay the balance",
              ],
              [
                "Re-delivery after a failed attempt caused by the customer",
                "A second delivery charge may apply — see section 6",
              ],
            ],
          },
        },
        `Any delivery charge is stated to you before you confirm your booking. We never add a delivery charge after an order has been confirmed.`,
      ],
    },
    {
      heading: "Timelines",
      body: [
        `Every e-cycle we hand over is assembled, charged, calibrated and pre-delivery inspected first. That preparation is what the timelines below allow for.`,
        {
          table: {
            head: ["Situation", "Ready for handover"],
            rows: [
              [
                "Product in stock at the showroom, pickup",
                "Same day or next working day after the booking advance is received",
              ],
              [
                "Product in stock, home delivery in Patna",
                "2 to 4 working days from booking confirmation",
              ],
              [
                "Product in stock, delivery to adjoining districts",
                "3 to 7 working days from booking confirmation",
              ],
              [
                "Product to be ordered in from the manufacturer",
                "7 to 21 working days, depending on the manufacturer's despatch schedule. We give you an expected date at the time of booking",
              ],
              [
                "Components and accessories only",
                "1 to 5 working days within Patna",
              ],
            ],
          },
        },
        `These are working-day estimates and exclude Sundays and public holidays. We contact you to fix a delivery slot before we despatch, and we tell you promptly if a date is going to slip.`,
        `Where a confirmed delivery date slips by more than 30 days for reasons on our side, you may cancel and receive a full refund — see our Refund & Cancellation Policy.`,
      ],
    },
    {
      heading: "Payment at delivery",
      body: [
        `Online payment on this website is a booking advance that reserves the unit in your name. The balance of the order value, plus any agreed delivery or accessory charge, is payable at the point of handover — at the showroom, or to our delivery representative before the product is handed over.`,
        {
          list: [
            "We do not release a product until the full price has been received. Ownership and risk pass to you on handover against full payment.",
            "The balance can be paid by UPI, card, or as otherwise agreed at the time of booking.",
            "A GST invoice is issued for the full order value at handover, showing the advance already paid and the balance received.",
            "Where the purchase is EMI-funded, handover takes place once the financier has disbursed or confirmed disbursal, and after you have signed the financier's documentation.",
          ],
        },
      ],
    },
    {
      heading: "What happens at handover — please check before you sign",
      body: [
        `Because we do not accept change-of-mind returns, the handover inspection matters. Take the time to do it. Our staff will go through it with you.`,
        {
          list: [
            "Check the packaging and the vehicle for any external damage, dent, scratch or crack before you accept it.",
            "Confirm the model, variant, colour, battery capacity and accessories match your order and your invoice.",
            "Check that the frame number and battery serial number on the vehicle match those recorded on your invoice and warranty card.",
            "Test the motor, throttle or pedal assist, brakes, lights, horn, display and gears with our staff present.",
            "Confirm you have received the charger, keys, warranty card, invoice, user manual and any accessory you paid for.",
            "Take a short video while unboxing a couriered component order — it is the simplest evidence if something is damaged inside.",
          ],
        },
        {
          note:
            "Transit damage or a wrong item must be reported at the time of delivery, or within 24 hours of handover at the very latest, with photographs. Reported within that window, we replace or repair it at our cost. Reported later, it cannot be distinguished from damage after handover and is not covered.",
        },
        `You or an authorised adult must be present to receive and sign for the delivery. The signed handover record is our proof of delivery.`,
      ],
    },
    {
      heading: "Failed and delayed deliveries",
      body: [
        {
          list: [
            "If nobody is available at the address at the agreed slot, or the address or contact number given is incorrect or unreachable, our representative will return the product to the showroom.",
            "We will contact you to arrange a second attempt. A second delivery charge may apply where the failure was on the customer's side.",
            "If a second attempt also fails and you do not respond to two written reminders, we may treat the booking as cancelled under our Refund & Cancellation Policy.",
            "Delivery may be delayed by events outside our control — floods, strikes, transport disruption, festival congestion, civil unrest or a manufacturer supply failure. We will tell you as soon as we know and give you a revised date.",
          ],
        },
      ],
    },
    {
      heading: "Battery transport, and why we do not ship far",
      body: [
        `Lithium-ion batteries — whether fitted to an e-cycle or supplied separately — are classified as dangerous goods (UN3480 / UN3481). Their carriage is regulated and most carriers restrict or refuse them.`,
        {
          list: [
            "We never despatch batteries or battery-equipped products by air.",
            "Battery-equipped products are moved only by our own vehicle or by a surface carrier equipped and licensed to carry them.",
            "We do not ship outside India, do not accept international orders, and do not act as an exporter of record.",
            "If you later export or carry a product abroad yourself, you are solely responsible for export licensing, destination-country import rules, customs duty, safety certification and the carrier's dangerous-goods requirements. The manufacturer's warranty is valid for service in India only.",
          ],
        },
      ],
    },
    {
      heading: "Questions about a delivery",
      body: [
        `Quote your order or invoice number and contact us on ${BUSINESS.phone} or at ${BUSINESS.email}. Our support hours are ${BUSINESS.supportHours}.`,
      ],
    },
  ];

  return (
    <PolicyPage
      title="Shipping & Delivery Policy"
      currentPath="/shipping-policy"
      intro={`Where we deliver, what it costs, how long it takes, what you should check at handover, and why battery-equipped products cannot be shipped long distance or exported.`}
      sections={sections}
    />
  );
}
